<template>
  <!-- Add Equipment Dialog with Enhanced Dropdown Template Selection -->
  <q-dialog :model-value="modelValue" persistent @update:model-value="$emit('update:modelValue', $event)">
    <q-card style="width: 700px; max-width: 90vw;">
      <q-card-section>
        <div class="text-h6">
          Add Equipment
          <span v-if="selectedSpace"> to {{ selectedSpace.name }}</span>
          <span v-else-if="selectedEquipment && formData.parent_equipment_id"> to {{ selectedEquipment.name }}</span>
        </div>
      </q-card-section>

      <q-card-section>
        <q-form @submit.prevent="handleSubmit">
          <!-- Template Selection - Now using dropdown like devices -->
          <q-select
            :model-value="formData.selectedTemplate"
            :options="equipmentTemplateOptions"
            label="Select Template (Optional)"
            filled
            clearable
            class="q-mb-md"
            hint="Choose a template to pre-populate equipment details"
            :loading="loadingTemplates"
            @update:model-value="handleTemplateSelect"
          >
            <template #prepend>
              <q-icon name="inventory_2" color="primary" />
            </template>
            <template #option="scope">
              <q-item v-bind="scope.itemProps">
                <q-item-section>
                  <q-item-label>{{ scope.opt.label }}</q-item-label>
                  <q-item-label v-if="scope.opt.description" caption>
                    {{ scope.opt.description }}
                  </q-item-label>
                  <q-item-label v-if="scope.opt.brick_class" caption>
                    Type: {{ scope.opt.brick_class }}
                  </q-item-label>
                </q-item-section>
              </q-item>
            </template>
            <template #no-option>
              <q-item>
                <q-item-section class="text-grey">
                  No templates available. Create equipment manually first, then convert to templates.
                </q-item-section>
              </q-item>
            </template>
          </q-select>

          <!-- Separator -->
          <q-separator class="q-my-md" />

          <!-- Template Preview -->
          <q-card v-if="formData.selectedTemplate" flat bordered class="q-mb-md">
            <q-card-section>
              <div class="text-subtitle2">Using Template: {{ formData.selectedTemplate.label }}</div>
              <div class="text-body2 text-grey-6 q-mb-sm">{{ formData.selectedTemplate.description || 'No description' }}</div>
              <div class="text-caption">
                BRICK Class: {{ formData.selectedTemplate.brick_class || 'Not specified' }}
                <span v-if="formData.selectedTemplate.default_points">
                  | Points: {{ Array.isArray(formData.selectedTemplate.default_points) ? formData.selectedTemplate.default_points.length : 0 }}
                </span>
              </div>
            </q-card-section>
          </q-card>

          <!-- Equipment Form Fields -->
          <q-input
            :model-value="formData.name"
            label="Equipment Name *"
            filled
            required
            class="q-mb-md"
            :hint="formData.selectedTemplate ? `Creating from template: ${formData.selectedTemplate.label}` : 'Name for this equipment'"
            @update:model-value="$emit('update:name', $event)"
          />

          <q-select
            :model-value="formData.brick_class"
            :options="equipmentBrickClasses"
            label="BRICK Class *"
            filled
            required
            emit-value
            map-options
            class="q-mb-md"
            hint="Select the BRICK ontology class for this equipment"
            :readonly="!!formData.selectedTemplate"
            @update:model-value="$emit('update:brick-class', $event)"
          />

          <!-- Space Selection -->
          <q-select
            :model-value="formData.space_id"
            :options="spaceOptions"
            label="Select Space for Equipment"
            filled
            required
            emit-value
            map-options
            class="q-mb-md"
            hint="Select the space where this equipment will be located"
            @update:model-value="$emit('update:space-id', $event)"
          />

          <q-input
            :model-value="formData.description"
            label="Description"
            filled
            type="textarea"
            rows="2"
            hint="Optional description"
            class="q-mb-md"
            @update:model-value="$emit('update:description', $event)"
          />

          <!-- Space Assignment for VAVs and Terminal Units (same as Zone interface) -->
          <q-select
            v-if="isVAVOrTerminalUnit(formData)"
            :model-value="formData.serves_space"
            :options="availableSpacesForZone"
            label="Space Served (Optional)"
            hint="Which space this terminal unit feeds"
            filled
            clearable
            emit-value
            map-options
            option-label="label"
            option-value="value"
            @update:model-value="$emit('update:serves-space', $event)"
          />
        </q-form>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat label="Cancel" @click="$emit('update:modelValue', false)" />
        <q-btn
          color="primary"
          :label="formData.selectedTemplate ? 'Create from Template' : 'Add Equipment'"
          :loading="saving"
          @click="handleSubmit"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
  import { defineProps, defineEmits } from 'vue';

  const props = defineProps({
    modelValue: {
      type: Boolean,
      required: true,
    },
    formData: {
      type: Object,
      required: true,
    },
    selectedSpace: {
      type: Object,
      default: null,
    },
    selectedEquipment: {
      type: Object,
      default: null,
    },
    equipmentTemplateOptions: {
      type: Array,
      default: () => [],
    },
    equipmentBrickClasses: {
      type: Array,
      required: true,
    },
    spaceOptions: {
      type: Array,
      required: true,
    },
    availableSpacesForZone: {
      type: Array,
      default: () => [],
    },
    loadingTemplates: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits([
    'update:modelValue',
    'update:name',
    'update:brick-class',
    'update:space-id',
    'update:description',
    'update:serves-space',
    'template-select',
    'submit',
  ]);

  const handleTemplateSelect = (template) => {
    emit('template-select', template);
  };

  const handleSubmit = () => {
    emit('submit');
  };

  const isVAVOrTerminalUnit = (equipment) => {
    if (!equipment || !equipment.brick_class) {return false;}
    const brickClass = equipment.brick_class.toLowerCase();
    return brickClass.includes('vav') ||
      brickClass.includes('variable_air_volume') ||
      brickClass.includes('terminal') ||
      brickClass.includes('fan_coil');
  };
</script>
